import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SvgIconProps,
  Tooltip,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import React, { useEffect, useMemo } from "react";
import {
  LabelerData,
  LabelingStatus,
  QCData,
} from "../features/api/labelApiSlice";
import { AnnotatorListItem } from "./AnnotatorListItem";
import { createStyleSheet } from "../utils/styles";
import { useTranslation } from "react-i18next";
import { formatStatusText } from "../features/data/conversationUtils";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAppSelector } from "../features/store";
import { useDispatch } from "react-redux";
import { setQC } from "../features/slices/labelSlice";

export type ConvoLabelers = Record<string, ConvoLabeler>;

export interface ConvoLabeler {
  labelers: LabelerData[];
  selectedLabeler?: LabelerData;
  inProgressLabelers?: LabelerData[];
}

export interface LabelerSelectProps {
  convoLabelers?: ConvoLabeler;
  qcData?: QCData[];
  handleLabelerChange?: (event: SelectChangeEvent) => void;
}

const LabelerSelect: React.FC<LabelerSelectProps> = ({
  convoLabelers,
  qcData,
  handleLabelerChange,
}) => {
  const { t } = useTranslation();
  const { isMonitor } = useCurrentUser();
  const qc = useAppSelector((state) => state.label.qc);
  const dispatch = useDispatch();
  const qcStatusMap = useMemo(
    () =>
      qcData?.reduce<Record<string, LabelingStatus>>((acc, qc) => {
        acc[qc.labeler_uid] = qc.qc_status;
        return acc;
      }, {}) ?? {},
    [qcData]
  );
  const qcCompleteList = useMemo(() => {
    if (isMonitor) {
      return qcData?.filter(
        (qc) =>
          qc.labeler_uid === convoLabelers?.selectedLabeler?.labeler_uid &&
          qc.qc_status === LabelingStatus.qc_complete
      );
    }
    return [];
  }, [isMonitor, qcData, convoLabelers?.selectedLabeler?.labeler_uid]);

  useEffect(() => {
    if (isMonitor && qcCompleteList?.length) {
      dispatch(setQC(qcCompleteList[0]));
    }
  }, [dispatch, isMonitor, qc, qcCompleteList]);

  const getQCStatusIcon = (labeler: LabelerData) => {
    const qcStatus = qcStatusMap[labeler.labeler_uid];

    const statusProps: Partial<
      Record<LabelingStatus, { color: SvgIconProps["color"]; label: string }>
    > = {
      [LabelingStatus.qc_complete]: {
        color: "success",
        label: formatStatusText(LabelingStatus.qc_complete),
      },
      [LabelingStatus.qc_in_progress]: {
        color: "warning",
        label: formatStatusText(LabelingStatus.qc_in_progress),
      },
    };

    const { color, label } = statusProps[qcStatus] ?? {
      color: "disabled",
      label: formatStatusText(LabelingStatus.qc_not_started),
    };

    return (
      <Tooltip title={label}>
        <CircleIcon fontSize="small" color={color} sx={styles.circleIcon} />
      </Tooltip>
    );
  };

  return (
    <Box sx={styles.labelerBox}>
      <FormControl fullWidth error={!convoLabelers?.selectedLabeler}>
        {convoLabelers?.selectedLabeler ? (
          <>
            <InputLabel id="labeler-label">Labeler</InputLabel>
            <Select
              labelId="labeler-label"
              value={convoLabelers.selectedLabeler.labeler_uid}
              label="Labeler"
              onChange={handleLabelerChange}
            >
              {convoLabelers.labelers.map((labeler) => (
                <MenuItem value={labeler.labeler_uid} key={labeler.labeler_uid}>
                  <Box sx={styles.labelerItemBox}>
                    {getQCStatusIcon(labeler)}
                    <AnnotatorListItem annotator={labeler} clickable={false} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </>
        ) : (
          <FormHelperText>
            {t("labelerSelect.labelerSelectErrorText")}
            <Box component="ul" sx={styles.inProgressList}>
              {convoLabelers?.inProgressLabelers?.map((labeler) => (
                <Box component="li" key={labeler.labeler_uid}>
                  {labeler.email}
                </Box>
              ))}
            </Box>
          </FormHelperText>
        )}
      </FormControl>
      {isMonitor && qcCompleteList?.length ? (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <>
            <InputLabel id="labeler-label">QC</InputLabel>
            <Select
              labelId="labeler-label"
              value={qc?.qc_uid ?? ""}
              label="Labeler"
              onChange={(e) =>
                dispatch(
                  setQC(qcCompleteList.find((qc) => qc.qc_uid === e.target.value))
                )
              }
            >
              {qcCompleteList.map((qc) => (
                <MenuItem value={qc.qc_uid} key={qc.qc_uid}>
                  <Box sx={styles.labelerItemBox}>
                    <AnnotatorListItem annotator={qc} clickable={false} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </>
        </FormControl>
      ) : (
        <></>
      )}
    </Box>
  );
};

const styles = createStyleSheet({
  labelerBox: { minWidth: 280, maxWidth: 400 },
  labelerItemBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  inProgressList: {
    margin: 0,
    paddingLeft: "10px",
  },
  circleIcon: {
    mr: 1,
  },
});

export default LabelerSelect;
